import React, { memo } from 'react';

import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import Table from '../../Table';

import ReportService from '../../../services/ReportService';
import createInvoiceScheduleTableCell from '../../../helpers/tables/createInvoiceScheduleTableCell';

const createLinkToDemand = (row) =>
  `/loans/${row?.loan_id}/demands/${row._loan?._demands?.[0]?.id}?tab=Demand`;

const createLinkToInvoice = (row) =>
  `/loans/${row?.loan_id}/invoices/${row?.id}?tab=Invoice`;

const createLinkToProperty = (row) => `/loans/${row?.loan_id}?tab=Property`;

const createBorrowerLink = (row) => `/loans/${row?.loan_id}?tab=Borrowers`;

const formatDate = (date) => moment(date).format('MM/DD/YYYY');

const createBorrowerTableCell = (borrower) => {
  if (!borrower) {
    return '';
  }

  if (borrower.TIN_type === 'EIN') {
    return borrower.entity_name;
  }
  return `${borrower.first_name || ''} ${borrower.last_name || ''}`;
};

function InvoicesLateTable() {
  const { t } = useTranslation();

  const { data: invoicesLate } = useQuery({
    queryKey: ['invoicesLate'],
    queryFn: ReportService.getLateInvoices,
    onError: (error) => {
      console.log('Error getting late invoices:', error);
    },
  });

  const cols = [
    {
      title: t('common.tables.columns.Due date'),
      keys: ['date'],
      link: createLinkToInvoice,
      format: formatDate,
    },
    {
      title: t('common.tables.columns.Late date'),
      keys: ['date_overdue'],
      link: createLinkToInvoice,
      format: formatDate,
    },
    {
      title: t('common.tables.columns.Schedule'),
      keys: ['scheduled_payments'],
      format: createInvoiceScheduleTableCell,
      link: createLinkToInvoice,
    },
    {
      title: t('common.tables.columns.Invoice number'),
      keys: ['pixel_invoice_number'],
      link: createLinkToInvoice,
    },
    {
      title: t('common.tables.columns.Status'),
      keys: ['status'],
      link: (row) =>
        row._loan?._demands?.[0]?.invoice_id &&
        row._loan?._demands?.[0]?.invoice_id.includes(row.id)
          ? createLinkToDemand(row)
          : createLinkToInvoice(row),
      badgeColor: () => 'amber',
      type: (row) => {
        if (
          row._loan?._demands?.[0]?.invoice_id &&
          row._loan?._demands?.[0]?.invoice_id.includes(row.id)
        ) {
          return 'badge';
        }
        return '';
      },
      tooltip: (row) =>
        row._loan?._demands?.[0]?.invoice_id &&
        row._loan?._demands?.[0]?.invoice_id.includes(row.id)
          ? 'This invoice is included in a demand.'
          : null,
    },
    {
      title: t('common.tables.columns.Total'),
      keys: ['_amount.invoice_total'],
      type: 'currency',
      link: createLinkToInvoice,
    },
    {
      title: t('common.tables.columns.Paid'),
      keys: ['_paid.amount_paid'],
      link: createLinkToInvoice,
      type: 'currency',
    },
    {
      title: t('common.tables.columns.Property'),
      keys: ['_loan._property.address_line_1'],
      link: createLinkToProperty,
    },
    {
      title: t('common.tables.columns.Borrower'),
      keys: ['_loan._borrower_primary'],
      format: createBorrowerTableCell,
      link: createBorrowerLink,
    },
  ];

  return (
    <div>
      <Table
        cols={cols}
        rows={invoicesLate}
        title={t('Dashboard.InvoicesLateTable.Invoices Late')}
      />
    </div>
  );
}

export default memo(InvoicesLateTable);
